import { Component, inject, OnInit } from '@angular/core';
import { AppAuthService, LowScoreFallbackRequiredError } from './app-auth.service';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatCardModule } from '@angular/material/card';
import { CommonModule } from '@angular/common';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatDividerModule } from '@angular/material/divider';
import { LowercaseDirective } from '../directives/lowercase.directive';
import { MatIconModule } from '@angular/material/icon';
import { AutotrimDirective } from '../directives/autotrim.directive';
import { NotificationService } from '../service/notification.service';
import { FormActionsComponent } from '../form-actions.component';
import { TranslateModule } from '@ngx-translate/core';
import { TranslationService } from '..';
import { RecaptchaV2FallbackComponent } from './recaptcha-v2-fallback.component';

@Component({
  selector: 'app-request-reset',
  templateUrl: 'request-reset.component.html',
  styleUrls: ['login-reset-register.component.scss'],
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatCardModule,
    MatInputModule,
    MatIconModule,
    MatButtonModule,
    MatDividerModule,
    LowercaseDirective,
    RouterModule,
    AutotrimDirective,
    FormActionsComponent,
    TranslateModule,
    RecaptchaV2FallbackComponent,
  ],
})
export class RequestResetComponent implements OnInit {
  private readonly toastr = inject(NotificationService);
  private readonly authService = inject(AppAuthService);
  private readonly translate = inject(TranslationService);
  form = new FormBuilder().nonNullable.group({
    email: ['', Validators.required],
  });
  route = inject(ActivatedRoute);

  get backToLoginQueryParams() {
    const returnUrl = this.route.snapshot?.queryParamMap?.get?.('returnUrl') ?? null;
    const email = this.form.controls.email.value;
    const params: Record<string, string> = {};
    if (returnUrl) {
      params['returnUrl'] = returnUrl;
    }
    if (email) {
      params['email'] = email;
    }
    return params;
  }
  invalid?: boolean = false;
  showV2Fallback = false;

  async resetRequest() {
    try {
      await this.authService.resetPasswordRequest(this.form.controls.email.value!);
      this.form.markAsPristine();
      this.toastr.success('Please_check_your_emails_tr');
    } catch (err) {
      if (err instanceof LowScoreFallbackRequiredError) {
        this.showV2Fallback = true;
        return;
      }
      throw err;
    }
  }

  async onV2Solved(token: string) {
    try {
      await this.authService.resetPasswordRequest(this.form.controls.email.value!, token);
      this.showV2Fallback = false;
      this.form.markAsPristine();
      this.toastr.success('Please_check_your_emails_tr');
    } catch (err) {
      if (err instanceof LowScoreFallbackRequiredError) {
        // server rejected v2 token too — leave widget visible so user can retry
        return;
      }
      this.showV2Fallback = false;
      throw err;
    }
  }

  ngOnInit(): void {
    const { email, invalid } = this.route.snapshot.queryParams;
    if (email) {
      this.form.patchValue({ email });
      this.form.markAsDirty();
    }
    this.invalid = invalid !== undefined;
  }
}
